import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface DropScrollResult {
  success: boolean;
  appeared: boolean; // Drop appeared in feed (not "won")
  collection_id?: string;
  collection_name?: string;
  rarity?: string;
  message?: string;
}

export interface DropClaimResult {
  won: boolean;
  claim_id?: string;
  rarity?: string;
  media_url?: string;
  error?: string;
  message?: string;
}

export function useDropScrollEvent() {
  const { user } = useAuth();
  const [isProcessing, setIsProcessing] = useState(false);
  const [lastResult, setLastResult] = useState<DropScrollResult | null>(null);

  const processScroll = async (): Promise<DropScrollResult | null> => {
    if (!user?.id || isProcessing) return null;

    try {
      setIsProcessing(true);

      const { data, error } = await supabase.rpc('process_drop_scroll_event', {
        p_user_id: user.id
      });

      if (error) {
        console.error('[Drop Engine] Scroll event error:', error);
        return null;
      }

      setLastResult(data);
      return data as DropScrollResult;
    } catch (error) {
      console.error('[Drop Engine] Unexpected error:', error);
      return null;
    } finally {
      setIsProcessing(false);
    }
  };

  const claimDrop = async (
    collectionId: string,
    rarity: string
  ): Promise<DropClaimResult | null> => {
    if (!user?.id || isProcessing) return null;

    try {
      setIsProcessing(true);

      const { data, error } = await supabase.rpc('claim_drop_from_feed', {
        p_user_id: user.id,
        p_collection_id: collectionId,
        p_rarity: rarity
      });

      if (error) {
        console.error('[Drop Engine] Claim error:', error);
        return { won: false, error: error.message };
      }

      return data as DropClaimResult;
    } catch (error) {
      console.error('[Drop Engine] Unexpected error:', error);
      return { won: false, error: 'Unexpected error occurred' };
    } finally {
      setIsProcessing(false);
    }
  };

  const getDropForFeed = async () => {
    if (!user?.id) return null;

    try {
      const { data, error } = await supabase.rpc('get_drop_for_feed', {
        p_user_id: user.id
      });

      if (error) {
        console.error('[Drop Engine] Get drop for feed error:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('[Drop Engine] Unexpected error:', error);
      return null;
    }
  };

  return {
    processScroll,
    claimDrop,
    getDropForFeed,
    isProcessing,
    lastResult,
  };
}
